/**
 * Router-compat shim - bridges @/lib/router-compat v6 call sites to
 * @tanstack/react-router without hand-rewriting every component.
 */
import {
  useNavigate as tsNavigate,
  useLocation as tsLocation,
  useParams as tsParams,
  useRouter,
  Link as TSLink,
  Navigate as TSNavigate,
  Outlet as TSOutlet,
} from "@tanstack/react-router";
import { useMemo, useCallback, forwardRef, type ComponentProps, type CSSProperties, type ReactNode } from "react";

// ---------- shared URL parsing ----------

function parseTo(to: string): {
  pathname: string;
  search: Record<string, string> | undefined;
  hash: string | undefined;
} {
  const [beforeHash, hashStr] = (to ?? "").split("#");
  const [pathname, searchStr] = (beforeHash ?? "").split("?");
  return {
    // react-router keeps the current path for search-only ("?a=1") and
    // hash-only ("#section") targets; TanStack's "." means current route.
    pathname: pathname || ".",
    search: searchStr ? Object.fromEntries(new URLSearchParams(searchStr)) : undefined,
    hash: hashStr || undefined,
  };
}

// ---------- useNavigate ----------

type NavigateOptions = { replace?: boolean; state?: unknown };

type NavigateFn = {
  (to: string | number, options?: NavigateOptions): void;
  (delta: number): void;
};

export function useNavigate(): NavigateFn {
  const tsNav = tsNavigate();
  const router = useRouter();
  return useCallback(
    (to: string | number, options?: NavigateOptions) => {
      if (typeof to === "number") {
        router.history.go(to);
        return;
      }
      const { pathname, search, hash } = parseTo(to);
      tsNav({
        to: pathname,
        search,
        hash,
        state: options?.state,
        replace: options?.replace,
      } as never);
    },
    [tsNav, router],
  ) as NavigateFn;
}

// ---------- useLocation ----------

export function useLocation() {
  const loc = tsLocation();
  return useMemo(
    () => ({
      pathname: loc.pathname,
      search: loc.searchStr ? `?${loc.searchStr}` : "",
      hash: loc.hash ?? "",
      state: (loc.state ?? null) as unknown,
      key: loc.pathname + (loc.searchStr ?? ""),
    }),
    [loc.pathname, loc.searchStr, loc.hash, loc.state],
  );
}

// ---------- useParams ----------

export function useParams<
  T extends Record<string, string | undefined> = Record<string, string | undefined>,
>(): T {
  return tsParams({ strict: false } as never) as T;
}

// ---------- useSearchParams (@/lib/router-compat compat) ----------

export function useSearchParams(): [
  URLSearchParams,
  (
    init: URLSearchParams | Record<string, string> | ((prev: URLSearchParams) => URLSearchParams),
    opts?: { replace?: boolean },
  ) => void,
] {
  const loc = tsLocation();
  const nav = tsNavigate();
  const router = useRouter();
  const params = useMemo(() => new URLSearchParams(loc.searchStr ?? ""), [loc.searchStr]);
  const setParams = useCallback(
    (
      init: URLSearchParams | Record<string, string> | ((prev: URLSearchParams) => URLSearchParams),
      opts?: { replace?: boolean },
    ) => {
      // Functional updaters read the router's live location, not the render
      // snapshot - react-router passes call-time params, and chained updates
      // within one tick must see each other's writes.
      const live = router.state.location;
      const current = new URLSearchParams(live.searchStr ?? "");
      const next =
        typeof init === "function"
          ? init(current)
          : init instanceof URLSearchParams
            ? init
            : new URLSearchParams(init);
      const searchObj: Record<string, string> = {};
      next.forEach((v, k) => {
        searchObj[k] = v;
      });
      nav({ to: live.pathname, search: searchObj, replace: opts?.replace } as never);
    },
    [nav, router],
  );
  return [params, setParams];
}

// ---------- Link ----------

type LinkProps = Omit<ComponentProps<typeof TSLink>, "to"> & {
  to: string;
  replace?: boolean;
  state?: unknown;
  children?: ReactNode;
};

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  { to, replace, state, children, ...rest },
  ref,
) {
  const { pathname, search, hash } = parseTo(to);
  const linkProps = {
    ref,
    to: pathname,
    search,
    hash,
    replace,
    state,
    ...(rest ?? {}),
  } as ComponentProps<typeof TSLink>;
  return <TSLink {...linkProps}>{children}</TSLink>;
});

// ---------- Navigate ----------

export function Navigate({
  to,
  replace,
  state,
}: {
  to: string;
  replace?: boolean;
  state?: unknown;
}) {
  const { pathname, search, hash } = parseTo(to);
  const navProps = {
    to: pathname,
    search,
    hash,
    state,
    replace,
  } as unknown as ComponentProps<typeof TSNavigate>;
  return <TSNavigate {...navProps} />;
}

// ---------- Outlet ----------

export const Outlet = TSOutlet;

// ---------- NavLink ----------

type NavLinkRenderState = { isActive: boolean; isPending: boolean; isTransitioning: boolean };

export type NavLinkProps = Omit<
  ComponentProps<"a">,
  "className" | "style" | "children" | "href"
> & {
  to: string;
  replace?: boolean | undefined;
  state?: unknown;
  end?: boolean | undefined;
  className?: string | ((state: NavLinkRenderState) => string | undefined) | undefined;
  style?:
    | CSSProperties
    | ((state: NavLinkRenderState) => CSSProperties | undefined)
    | undefined;
  children?: ReactNode | ((state: NavLinkRenderState) => ReactNode) | undefined;
};

export const NavLink = forwardRef<HTMLAnchorElement, NavLinkProps>(function NavLink(
  { to, end, className, style, children, ...rest },
  ref,
) {
  const loc = tsLocation();
  const { pathname } = parseTo(to);
  const current = loc.pathname.replace(/\/+$/, "") || "/";
  const target = pathname.replace(/\/+$/, "") || "/";
  const isActive = end ? current === target : current === target || current.startsWith(`${target}/`);
  const state: NavLinkRenderState = { isActive, isPending: false, isTransitioning: false };

  return (
    <Link
      ref={ref}
      to={to}
      className={typeof className === "function" ? className(state) : className}
      style={typeof style === "function" ? style(state) : style}
      aria-current={isActive ? "page" : undefined}
      {...(rest as Record<string, unknown>)}
    >
      {typeof children === "function" ? children(state) : children}
    </Link>
  );
});
